/**
 * BaseSelect – reusable select built on Radix UI Select (shadcn-style).
 * All styling uses project theme (purple _variables.scss equivalents).
 * @see https://ui.shadcn.com/docs/components/radix/select
 */

import React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'

/* Theme-aligned styles (from _variables.scss); min-height/padding align with chart search inputs */
const triggerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.35rem',
    boxSizing: 'border-box',
    minHeight: '3rem',
    padding: '0.75rem 1rem',
    fontSize: '1.125rem',
    lineHeight: 1.5,
    color: '#2B2B3A',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E9E1E9',
    borderRadius: '8px',
    outline: 'none',
    cursor: 'pointer',
    minWidth: '12rem',
}
const contentStyles: React.CSSProperties = {
    position: 'relative',
    zIndex: 10050,
    maxHeight: 'var(--radix-select-content-available-height)',
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E9E1E9',
    borderRadius: '8px',
    boxShadow: '0 4px 16px hsl(240 5% 15% / 0.12)',
}
const itemStyles: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    padding: '0.625rem 1rem',
    fontSize: '1.125rem',
    color: '#2B2B3A',
    cursor: 'pointer',
    outline: 'none',
}

const triggerSuffixStyle: React.CSSProperties = {
    display: 'inline-flex',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '0.2rem',
    marginLeft: '0.25rem',
}

const clearIconStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '1.75rem',
    height: '1.75rem',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#ffffff',
    fontSize: '1.35rem',
    fontWeight: 300,
    lineHeight: 0,
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.25)',
}

const chevronIconStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '1.125rem',
    height: '1.75rem',
    opacity: 0.6,
    lineHeight: 0,
    fontSize: '10px',
}

const SelectTriggerInner = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
        style?: React.CSSProperties
        showClear?: boolean
        onClear?: () => void
    }
>(({ style, children, className, showClear, onClear, ...props }, ref) => (
    <SelectPrimitive.Trigger ref={ref} className={className} style={{ ...triggerStyles, ...style }} {...props}>
        <span
            style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                minHeight: '1.5em',
            }}
        >
            {children}
        </span>
        <span style={triggerSuffixStyle}>
            {showClear ? (
                <span
                    onPointerDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onClear?.()
                    }}
                    style={clearIconStyle}
                    aria-hidden
                    title="Clear"
                >
                    ×
                </span>
            ) : null}
            <SelectPrimitive.Icon asChild>
                <span style={chevronIconStyle} aria-hidden>
                    ▼
                </span>
            </SelectPrimitive.Icon>
        </span>
    </SelectPrimitive.Trigger>
))
SelectTriggerInner.displayName = 'SelectTrigger'

const SelectContentInner = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & { style?: React.CSSProperties }
>(({ style, position = 'popper', children, ...props }, ref) => (
    <SelectPrimitive.Portal>
        <SelectPrimitive.Content ref={ref} position={position} style={{ ...contentStyles, ...style }} {...props}>
            <SelectPrimitive.Viewport
                style={{
                    padding: '4px',
                    ...(position === 'popper' && {
                        width: 'var(--radix-select-trigger-width)',
                        height: 'var(--radix-select-content-available-height)',
                        maxHeight: 'var(--radix-select-content-available-height)',
                    }),
                }}
            >
                {children}
            </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
))
SelectContentInner.displayName = 'SelectContent'

const SelectItemInner = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ style, children, ...props }, ref) => (
    <SelectPrimitive.Item ref={ref} style={{ ...itemStyles, ...style }} {...props}>
        <SelectPrimitive.ItemText asChild>
            <span>{children}</span>
        </SelectPrimitive.ItemText>
        <SelectPrimitive.ItemIndicator style={{ marginLeft: 'auto', fontSize: '12px' }}>✓</SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
))
SelectItemInner.displayName = 'SelectItem'

export interface BaseSelectOption {
    value: string
    label: string
}

export interface BaseSelectProps {
    /** Options to display (value + label) */
    options: BaseSelectOption[]
    /** Controlled value */
    value?: string
    /** Default value when uncontrolled */
    defaultValue?: string
    /** Called when selection changes */
    onValueChange?: (value: string) => void
    /** Placeholder when nothing selected */
    placeholder?: string
    /** Disable the select */
    disabled?: boolean
    /** HTML id for the trigger (e.g. for labels) */
    id?: string
    /** Form field name (submitted with form) */
    name?: string
    /** Trigger width (default: min 8rem, can override with e.g. "100%") */
    triggerStyle?: React.CSSProperties
    /** Open state when controlled */
    open?: boolean
    /** Called when open state changes */
    onOpenChange?: (open: boolean) => void
    /** Mark as invalid for a11y and styling */
    'aria-invalid'?: boolean
    /** Show a clear (×) control when value differs from clearValue (e.g. reset to "all"). */
    allowClear?: boolean
    /** Value passed to onValueChange when clear is used; must match an option value. */
    clearValue?: string
    /** Merged onto the trigger element (in addition to `has_value` when applicable). */
    triggerClassName?: string
}

export function BaseSelect({
    options,
    value,
    defaultValue,
    onValueChange,
    placeholder = 'Select…',
    disabled = false,
    id,
    name,
    triggerStyle,
    open,
    onOpenChange,
    'aria-invalid': ariaInvalid,
    allowClear = false,
    clearValue,
    triggerClassName,
}: BaseSelectProps) {
    const effectiveValue = value !== undefined ? value : defaultValue ?? ''
    const showClear =
        allowClear &&
        clearValue !== undefined &&
        effectiveValue !== '' &&
        effectiveValue !== clearValue

    const isNeutralSelection =
        effectiveValue === '' || (clearValue !== undefined && effectiveValue === clearValue)
    const hasValue = !isNeutralSelection && effectiveValue !== ''

    const handleClear = () => {
        if (clearValue !== undefined) onValueChange?.(clearValue)
    }

    /** Always `base_select`; add `has_value` when not neutral; then optional `triggerClassName`. */
    const triggerClassMerged = ['base_select', hasValue ? 'has_value' : '', triggerClassName ?? '']
        .filter((c): c is string => typeof c === 'string' && c.trim().length > 0)
        .join(' ')

    return (
        <SelectPrimitive.Root
            value={value}
            defaultValue={defaultValue}
            onValueChange={onValueChange}
            disabled={disabled}
            name={name}
            open={open}
            onOpenChange={onOpenChange}
        >
            <SelectTriggerInner
                id={id}
                className={triggerClassMerged}
                style={triggerStyle}
                aria-invalid={ariaInvalid}
                showClear={showClear}
                onClear={handleClear}
            >
                <SelectPrimitive.Value placeholder={placeholder} />
            </SelectTriggerInner>
            <SelectContentInner position="popper">
                {options.map((opt) => (
                    <SelectItemInner key={opt.value} value={opt.value}>
                        {opt.label}
                    </SelectItemInner>
                ))}
            </SelectContentInner>
        </SelectPrimitive.Root>
    )
}
